'use client';

import { Video } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { claimLive } from '@/app/apis';
import { App } from '@/app/components/icons/App';
import { Loading } from '@/app/components/icons/Loading';
import { useToast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import { type LiveAvatar, LiveClaimError, type UserInfo } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { WechatProvider } from '../../../../../h5/_shared/context/WechatContext';
import { useDownload } from '../../../../../h5/_shared/hook/use-download';
import {
  type ContactFormError,
  getComplianceStep,
  getContactFormError,
  getIdentityFormError,
  type IdentityFormError,
  LiveComplianceModal,
} from '../../../_components/compliance';
import { LiveCallConsentModal } from '../../../_components/LiveCallConsentModal';
import { hasLiveCallConsent, saveLiveCallConsent } from '../../../_hooks/live-call-consent';
import { getAvatarInitial } from '../../../[id]/_utils/live-call';
import {
  confirmLiveSeniorNotice,
  getLiveComplianceStatus,
  getPublicAvatar,
  submitLiveEmergencyContact,
  verifyLiveIdentity,
} from '../../../apis';
import type { LiveV1ComplianceStatusReply } from '../../../types';
import { isUserBannerAvatar } from '../../../utils';
import type { ShareAvatar } from '../type';

interface ShareLandingProps {
  avatarId: string;
}

// 「打开 App」按钮：useDownload 依赖 WechatProvider（微信安卓走 wx-open-launch-app 拉起），
// 因此只在这里包一层 provider，作用域最小，不影响页面主体。
function OpenInAppButtonInner({ avatarId }: { avatarId: string }) {
  const t = useTranslations();
  const { callAppContent } = useDownload({
    proAppTarget: `avatar-detail?id=${avatarId}`,
    renderCallAppContent: () => (
      <button
        type="button"
        onClick={() => {
          reportEvent('get_app_click', { click_position: 'live_share_landing', stream_character_id: avatarId });
        }}
        className="hidden h-12 w-[210px] items-center justify-center gap-2 rounded-full bg-[#4370FF] px-8 font-medium text-sm text-white transition hover:bg-[#4370FF]/90 md:inline-flex"
      >
        <App className="text-lg" />
        {t('vidu-stream.share.landing.openInApp')}
      </button>
    ),
  });

  return <>{callAppContent}</>;
}

function OpenInAppButton({ avatarId }: { avatarId: string }) {
  return (
    <WechatProvider>
      <OpenInAppButtonInner avatarId={avatarId} />
    </WechatProvider>
  );
}

export function ShareLanding({ avatarId }: ShareLandingProps) {
  const t = useTranslations();
  const { toast } = useToast();
  const router = useRouter();
  const storeApi = useStoreApi();
  const { userInfo, setLiveAvatar, setLiveSession } = useStore((s) => ({
    userInfo: s.userInfo,
    setLiveAvatar: s.setLiveAvatar,
    setLiveSession: s.setLiveSession,
  }));
  const { open: openLoginDialog } = useGlobalModal('loginDialog');

  const [avatar, setAvatar] = useState<ShareAvatar | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [complianceStatus, setComplianceStatus] = useState<LiveV1ComplianceStatusReply | null>(null);
  const [isComplianceSubmitting, setIsComplianceSubmitting] = useState(false);
  const [contactError, setContactError] = useState<ContactFormError>();
  const [identityError, setIdentityError] = useState<IdentityFormError>();
  const pendingActionRef = useRef<(() => void | Promise<void>) | null>(null);
  const pendingUserIdRef = useRef<string | null>(null);
  const pendingComplianceActionRef = useRef<(() => void | Promise<void>) | null>(null);

  const initAvatar = useCallback(
    async (isAlive: () => boolean) => {
      setIsLoading(true);
      setNotFound(false);
      try {
        const { data: avatarData, error } = await getPublicAvatar(avatarId);
        if (!isAlive()) return;
        if (error || !avatarData?.avatar?.id) {
          setNotFound(true);
          return;
        }
        setAvatar(avatarData.avatar);
        reportEvent('page_view', {
          page_name: 'share_live_page',
          stream_character_name: avatarData.avatar.name?.trim() || 'Avatar',
          stream_character_id: avatarData.avatar.id,
          stream_character_type: isUserBannerAvatar(avatarData.avatar) ? 'ugc' : 'pgc',
        });
      } catch {
        if (isAlive()) setNotFound(true);
      } finally {
        if (isAlive()) setIsLoading(false);
      }
    },
    [avatarId],
  );

  useEffect(() => {
    let alive = true;
    initAvatar(() => alive);
    return () => {
      alive = false;
    };
  }, [initAvatar]);

  // 登录后先完成国内站合规准入，再展示视频通话个人信息处理授权。
  const runWithConsent = useCallback((action: () => void | Promise<void>, userId: string) => {
    if (hasLiveCallConsent(userId)) {
      void action();
      return;
    }
    pendingUserIdRef.current = userId;
    pendingActionRef.current = action;
    setConsentOpen(true);
  }, []);

  const handleConsentConfirm = useCallback(() => {
    const userId = pendingUserIdRef.current ?? storeApi.getState().userInfo?.id;
    if (userId) {
      saveLiveCallConsent(userId);
    }
    pendingUserIdRef.current = null;
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action) {
      void action();
    }
  }, [storeApi]);

  const completeCompliance = useCallback((status: LiveV1ComplianceStatusReply) => {
    setComplianceStatus(status);
    if (getComplianceStep(status) !== 'ready') {
      setComplianceOpen(true);
      return;
    }
    setComplianceOpen(false);
    const action = pendingComplianceActionRef.current;
    pendingComplianceActionRef.current = null;
    if (action) void action();
  }, []);

  const runWithCompliance = useCallback(
    async (action: () => void | Promise<void>) => {
      if (SITE !== 'china') {
        await action();
        return;
      }
      pendingComplianceActionRef.current = action;
      setIsComplianceSubmitting(true);
      const { data, error } = await getLiveComplianceStatus();
      setIsComplianceSubmitting(false);
      if (error || !data) {
        pendingComplianceActionRef.current = null;
        toast({ type: 'error', message: '合规状态获取失败，请稍后重试。' });
        return;
      }
      completeCompliance(data);
    },
    [completeCompliance],
  );

  const submitIdentity = useCallback(
    async (body: { real_name: string; id_no: string }) => {
      setIsComplianceSubmitting(true);
      setIdentityError(undefined);
      const { data, error } = await verifyLiveIdentity(body);
      setIsComplianceSubmitting(false);
      if (error || !data) {
        setIdentityError(getIdentityFormError(error?.reason));
        return;
      }
      completeCompliance(data);
    },
    [completeCompliance, t, toast],
  );

  const submitContact = useCallback(
    async (body: { name: string; phone: string; relationship: string }) => {
      setIsComplianceSubmitting(true);
      setContactError(undefined);
      const { data, error } = await submitLiveEmergencyContact(body);
      setIsComplianceSubmitting(false);
      if (error || !data) {
        setContactError(getContactFormError(error?.reason));
        return;
      }
      completeCompliance(data);
    },
    [completeCompliance, t, toast],
  );

  const confirmSeniorNotice = useCallback(async () => {
    setIsComplianceSubmitting(true);
    const { data, error } = await confirmLiveSeniorNotice();
    setIsComplianceSubmitting(false);
    if (error || !data) {
      toast({ type: 'error', message: '确认失败，请稍后重试。' });
      return;
    }
    completeCompliance(data);
  }, [completeCompliance, t, toast]);

  const handleComplianceOpenChange = useCallback((open: boolean) => {
    setComplianceOpen(open);
    if (!open) {
      pendingComplianceActionRef.current = null;
      setIdentityError(undefined);
      setContactError(undefined);
    }
  }, []);

  const doClaim = useCallback(async () => {
    if (!avatar?.id || isStarting) return;
    setIsStarting(true);
    try {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        toast({ type: 'error', message: t('recorder.accessUnsupported') });
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => {
        track.stop();
      });

      const avatarUrl = avatar.avatar_url?.trim() || '';
      const claim = await claimLive({ avatar_id: avatar.id, call_mode: 'video' });
      // 落地页只有简化版角色数据，构造一个最小 LiveAvatar 供通话页展示。
      const roleForStore = { id: avatar.id, name: avatar.name?.trim() || 'Avatar', image_uri: avatarUrl } as LiveAvatar;
      setLiveSession({
        avatarId: avatar.id,
        avatarUrl: avatarUrl || undefined,
        claimedAt: new Date().toISOString(),
        claim,
      });
      setLiveAvatar(roleForStore);
      // 带上 avatar_id：结束页刷新 / 重进后内存 store 已空，可据此回查角色补全头像与简介。
      router.push(`/vidu-stream/${claim.live.id}?avatar_id=${encodeURIComponent(avatar.id)}`);
    } catch (error) {
      const message =
        error instanceof LiveClaimError && error.code === 'request_failed'
          ? error.message.trim() || t('live.claimError')
          : error instanceof Error
            ? error.message
            : t('live.claimError');
      toast({ type: 'error', message });
    } finally {
      setIsStarting(false);
    }
  }, [avatar, isStarting, router, setLiveAvatar, setLiveSession, t, toast]);

  const handleStartCall = () => {
    if (!avatar) return;
    reportEvent('stream_element_click', {
      element_name: 'share_startcall_click',
      stream_character_name: avatar.name?.trim() || 'Avatar',
      stream_character_id: avatar.id ?? '',
      stream_character_type: isUserBannerAvatar(avatar) ? 'ugc' : 'pgc',
    });
    const currentUser: UserInfo | null = userInfo ?? null;
    if (!currentUser) {
      openLoginDialog({
        onClose: (loggedInUser?: UserInfo) => {
          if (loggedInUser) {
            void runWithCompliance(() => runWithConsent(doClaim, loggedInUser.id));
          }
        },
      });
      reportEvent('login_pop_exposure', {
        refer_element: 'share_startcall_click',
        refer_page: 'live_share_landing',
        stream_character_id: avatar.id ?? '',
      });
      return;
    }
    void runWithCompliance(() => runWithConsent(doClaim, currentUser.id));
  };

  if (isLoading) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-black">
        <Loading className="animate-spin text-4xl text-white/60" />
      </main>
    );
  }

  if (notFound || !avatar) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-black px-6 text-center text-white">
        <p className="text-base text-system-white48">{t('vidu-stream.share.landing.notFound')}</p>
      </main>
    );
  }

  const name = avatar.name?.trim() || 'Avatar';
  // 优先取个人主页照片描述，缺失时回退 intro
  const intro =
    (
      avatar as typeof avatar & { profile_photos?: Array<{ description?: string }> }
    ).profile_photos?.[0]?.description?.trim() ||
    avatar.intro?.trim() ||
    '';
  const avatarUrl = avatar.avatar_url?.trim() || '';

  return (
    <main className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-black px-6 text-center text-white">
      {/* 背景：角色图撑满 + 强模糊 + 低透明度 */}
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-[0.16] blur-[60px]"
        />
      ) : null}

      <div className="flex min-h-dvh flex-col items-center justify-between">
        <div />

        <div className="relative z-10 flex w-full max-w-[360px] flex-col items-center">
          <div className="relative aspect-square w-[100px] overflow-hidden rounded-full border border-white/15 bg-white/[0.06]">
            {avatarUrl ? (
              <>
                <img
                  src={avatarUrl}
                  alt=""
                  aria-hidden
                  className="absolute inset-0 h-full w-full scale-110 object-contain blur-2xl"
                />
                <img src={avatarUrl} alt={name} className="relative z-10 h-full w-full object-contain" />
              </>
            ) : (
              <span className="flex h-full w-full items-center justify-center font-semibold text-4xl text-white/80">
                {getAvatarInitial(name)}
              </span>
            )}
          </div>

          <h1 className="mt-6 font-medium text-2xl text-white">
            {t('vidu-stream.share.landing.videoCallWith', { name })}
          </h1>
          {intro ? <p className="mt-3 line-clamp-2 text-sm text-white/60 leading-relaxed">{intro}</p> : null}

          <button
            type="button"
            onClick={handleStartCall}
            disabled={isStarting}
            className="mt-8 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-8 font-medium text-black text-sm transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Video className="size-5" />
            {t('vidu-stream.share.landing.startCall')}
          </button>
        </div>

        <div className="flex items-center justify-center pb-20">
          <OpenInAppButton avatarId={avatar.id ?? ''} />
        </div>
      </div>
      <LiveCallConsentModal open={consentOpen} onOpenChange={setConsentOpen} onConfirm={handleConsentConfirm} />
      {SITE === 'china' && (
        <LiveComplianceModal
          open={complianceOpen}
          onOpenChange={handleComplianceOpenChange}
          status={complianceStatus}
          submitting={isComplianceSubmitting}
          contactError={contactError}
          identityError={identityError}
          onContactErrorChange={setContactError}
          onIdentityErrorChange={setIdentityError}
          onIdentitySubmit={submitIdentity}
          onContactSubmit={submitContact}
          onSeniorNoticeConfirm={confirmSeniorNotice}
        />
      )}
    </main>
  );
}
